import type { FunctionComponent, ReactNode } from "react";
import { graphql, useFragment } from "react-relay";

import { checkIsPostiesArticle } from "~/components/article/article-render/posties/helpers";
import { checkIsStyleArticle } from "~/components/article/article-render/style-article/helper";
import { article as articleData } from "~/data/article";
import { useArticleUtils } from "~/lib/hooks/article-utils";
import type { articleFooterArticle$key } from "~/queries/__generated__/articleFooterArticle.graphql";

import { DynamicPostiesArticleFooter } from "./dynamics";
import { StyledGenericArticleFooter, StyledMorningStudioArticleFooter } from "./styles";

type Props = {
  articleRecommendation?: {
    articleIds: string[];
    recommendationId: string;
  };
  className?: string;
  divider?: ReactNode;
  reference: articleFooterArticle$key;
};
export const ArticleFooter: FunctionComponent<Props> = ({
  articleRecommendation,
  className,
  divider,
  reference: reference_,
}) => {
  const article = useFragment(
    graphql`
      fragment articleFooterArticle on Article {
        ...articleUtilsArticle
        ...articleMoreStoriesArticle
        ...morningStudioArticleFooterContent
        ...genericArticleFooterArticle
        ...helpersCheckIsPostiesArticle
        ...helpersCheckIsStyleArticle
      }
    `,
    reference_,
  );

  const { getArticleType } = useArticleUtils();

  if (checkIsPostiesArticle(article)) {
    return <DynamicPostiesArticleFooter className={className} reference={article} />;
  }

  if (checkIsStyleArticle(article)) {
    return null;
  }

  switch (getArticleType(article)) {
    case articleData.types.live.entityId:
      return null;
    case articleData.types.studio.entityId:
      return (
        <>
          {divider}
          <StyledMorningStudioArticleFooter className={className} reference={article} />
        </>
      );
    default:
      return articleRecommendation?.articleIds.length ? (
        <>
          {divider}
          <StyledGenericArticleFooter
            className={className}
            {...articleRecommendation}
            reference={article}
          />
        </>
      ) : null;
  }
};

ArticleFooter.displayName = "ArticleFooter";
